import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { useParams, useNavigate } from 'react-router-dom';

// Ajouter CadrageUpdate à l'import
import { UseCaseDetail, NewInterviewData, UseCaseStatus, Interview } from '../../types';
import { useCadrageStore } from '../../stores/cadrageStore'; // Garder pour certaines actions spécifiques si non déplacées

// Importer les nouveaux hooks
import { useCadrageData } from '../../hooks/useCadrageData';
import { useCadrageTeamManagement } from '../../hooks/useCadrageTeamManagement';
import { useCadrageGuidedInterviews } from '../../hooks/useCadrageGuidedInterviews';


// Importer les nouveaux composants de section
import CadrageGeneralInfoSection from '../../components/Cadrages/DetailSections/CadrageGeneralInfoSection';
import CadrageLinkedUseCaseSection from '../../components/Cadrages/DetailSections/CadrageLinkedUseCaseSection';
import CadrageCompanyInfoSection from '../../components/Cadrages/DetailSections/CadrageCompanyInfoSection'; // Importer le nouveau composant
import CadrageTeamSection from '../../components/Cadrages/DetailSections/CadrageTeamSection';
import CadrageArchitectureFlowSection from '../../components/Cadrages/DetailSections/CadrageArchitectureFlowSection';
import CadrageDatabaseSchemaSection from '../../components/Cadrages/DatabaseSchemaSection'; // Utiliser directement
import CadrageInterviewsSection from '../../components/Cadrages/DetailSections/CadrageInterviewsSection';
import DocumentManagerSection from '../../components/Documents/DocumentManagerSection';
import CadrageBlockersSection from '../../components/Cadrages/DetailSections/CadrageBlockersSection';
import CadrageHistorySection from '../../components/Cadrages/DetailSections/CadrageHistorySection'; // Import du composant d'historique
import notify from '@/lib/notify';
import { readStoredDocument } from '@/services/documentReaderService';
import { useTranslation } from 'react-i18next';
import cadrageService from '@/services/cadrageService';

// Importer les modales
import { NewInterviewModal } from '../../components/Interviews/NewInterviewModal';
import { InterviewEditModal } from '../../components/Interviews/InterviewEditModal'; // Importer la modale d'édition
import UseCaseFormModal from '../../components/UseCases/UseCaseFormModal';

// Importer les icônes nécessaires pour les onglets
import { Archive, ArrowLeft, BookOpen, Building2, Clock, Database, Eye, FileText, Info, Lock, MessageCircle, Server } from 'lucide-react';
import { AskAgentButton } from '../../components/Common/AskAgentButton';
import { PageLayout } from '../../components/Layout/PageLayout';
import { NotFoundState } from '../../components/Common/NotFoundState';
import { ConfirmationModal } from '../../components/Common/ConfirmationModal';
import { ApplicationManagementModule, ApplicationSelector } from '../../components/Forge';
import forgeService from '../../services/forgeService';
import { domainService } from '../../services/domainService';
import { Button } from '../../components/Common/button';
import { useAuthStore } from '../../stores/authStore';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';

const CadrageDetailPage: React.FC = () => {
  const { id: cadrageId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // --- Utilisation des Hooks ---
  const { cadrageData, isLoading: isLoadingCadrage, error: errorCadrage, loadError: loadErrorCadrage, refetch: refetchCadrageData } = useCadrageData(cadrageId);
  const { teamMembers, availableUsers, isLoading: isTeamLoading, error: teamError, addMember, removeMember } = useCadrageTeamManagement(cadrageId);
  const interviewsHook = useCadrageGuidedInterviews(cadrageId);

  // Récupérer le UseCase lié depuis le store (peut être intégré dans useCadrageData à terme)
  const linkedUseCaseDetail = useCadrageStore((state) => state.currentUseCaseDetail);
  // Action pour ajouter un use case lié (gardée du store pour l'instant)
  const addLinkedUseCaseAction = useCadrageStore((state) => state.addLinkedUseCase);


  // --- États locaux ---
  const [isNewInterviewModalOpen, setIsNewInterviewModalOpen] = useState(false);
  const [isUseCaseFormModalOpen, setIsUseCaseFormModalOpen] = useState(false);
  const [interviewToView, setInterviewToView] = useState<Interview | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Delete states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [cadrageNameInput, setCadrageNameInput] = useState('');

  // Handler pour la soumission de la modale de Nouvel Entretien
  const handleModalCreateInterview = async (data: Partial<NewInterviewData>) => {
    try {
      await interviewsHook.createGuidedInterview(data);
      setIsNewInterviewModalOpen(false); // Fermer en cas de succès
    } catch (error) {
      console.error(t('cadrage.detail.errors.interviewCreationError'), error);
      notify.error(`${t('cadrage.detail.errorPrefix')} ${error instanceof Error ? error.message : String(error)}`);
      // Garder la modale ouverte en cas d'erreur
    }
  };

  // Handler pour la soumission de la modale de Création de Use Case
  const handleModalCreateUseCase = (data: UseCaseDetail) => {
    // Logique de traitement et d'ajout via l'action du store
    const processedData = {
      ...data,
      value_score: data.value_score ? Number(data.value_score) : undefined,
      complexity_score: data.complexity_score ? Number(data.complexity_score) : undefined,
      status: (data.status || 'Identified') as UseCaseStatus,
      evaluation_data: data.evaluation_data?.map(item => ({ ...item, score: item.score ? Number(item.score) : 0 })) || [],
      analysis_data: data.analysis_data || { strengths: [], weaknesses: [] },
      description: data.description || '',
      business_need: data.business_need || '',
      input: data.input || '',
      output: data.output || '',
      involved_tools: data.involved_tools || [],
      technical_elements: data.technical_elements || '',
      recommendation: data.recommendation || '',
      personas: data.personas || [],
      cadrageId: cadrageId, // Lier au cadrage actuel (utiliser la variable du scope)
    };
    const newId = `uc-${Date.now()}`;
    const newUseCase = { ...processedData, id: newId };

    console.log("Ajout du nouveau cas d'usage lié au Cadrage:", newUseCase);
    addLinkedUseCaseAction(newUseCase); // Appeler l'action du store

    setIsUseCaseFormModalOpen(false); // Fermer la modale
    // Optionnel: Naviguer vers la page du nouveau cas d'usage
    navigate(`/cas-usage/${newId}`);
  };

  // Handler pour la sauvegarde depuis la modale d'édition d'entretien
  const handleSaveEditedInterview = async (interviewId: string, data: any) => { // Utiliser 'any' temporairement si InterviewUpdate pose problème
    try {
      await interviewsHook.updateInterview(interviewId, data);
      interviewsHook.closeEditModal(); // Fermer la modale via le hook
    } catch (error) {
      notify.error(`${t('cadrage.detail.errorPrefix')} ${error instanceof Error ? error.message : String(error)}`);
      // La modale reste ouverte car l'erreur est levée par le hook
    }
  };

  // Application error detection for the linked use case's application
  const [appLoadError, setAppLoadError] = useState<string | null>(null);
  const [domainForgeId, setDomainForgeId] = useState<string | null>(null);
  const [showChangeApp, setShowChangeApp] = useState(false);
  const hasDomainForge = forgeApiEnabled && Boolean(domainForgeId);

  // Delete handlers
  const handleDeleteCadrage = () => {
    setCadrageNameInput('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!cadrageId || !cadrageData) return;

    try {
      setIsDeleting(true);

      await cadrageService.deleteCadrage(cadrageId);

      // Navigate back to cadrages list with success message
      navigate('/cadrages', {
        state: { message: t('cadrageDetail.deleteSuccess', { name: cadrageData.name }) }
      });
    } catch (err: any) {
      console.error('Failed to delete cadrage', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setCadrageNameInput('');
  };
  const [pendingAppId, setPendingAppId] = useState<string | null>(null);
  const [isSavingApp, setIsSavingApp] = useState(false);
  const [isUnlinkingApp, setIsUnlinkingApp] = useState(false);

  const linkedAppId = linkedUseCaseDetail?.forge_application_id ?? null;
  const isReadOnly = cadrageData ? !cadrageData.is_active : false;

  useEffect(() => {
    if (!linkedAppId || !forgeApiEnabled) { setAppLoadError(null); return; }
    let ignore = false;
    forgeService.getApplication(linkedAppId)
      .then(() => { if (!ignore) setAppLoadError(null); })
      .catch((err: any) => {
        if (!ignore) {
          // Si l'application n'existe plus (404), afficher un message spécifique
          if (err.response?.status === 404) {
            setAppLoadError(t('cadrage.detail.application.notFound', 'Application Forge non trouvée (peut-être supprimée)'));
          } else {
            setAppLoadError(err?.message || t('cadrage.detail.application.loadError', 'Impossible de charger l\'application associée.'));
          }
        }
      });
    return () => { ignore = true; };
  }, [linkedAppId, forgeApiEnabled, t]);

  useEffect(() => {
    const domainId = (linkedUseCaseDetail as any)?.domain_id as string | undefined;
    if (!domainId) {
      setDomainForgeId(null);
      return;
    }
    let ignore = false;
    domainService.getDomainDetails(domainId)
      .then((domain) => {
        if (!ignore) {
          setDomainForgeId((domain as any).forge_id ?? null);
        }
      })
      .catch(() => {
        if (!ignore) {
          setDomainForgeId(null);
        }
      });
    return () => { ignore = true; };
  }, [linkedUseCaseDetail]);

  useEffect(() => {
    const hasHistoryTab = !isReadOnly;
    const maxTabIndex = hasDomainForge
      ? (hasHistoryTab ? 7 : 6)
      : (hasHistoryTab ? 6 : 5);
    if (selectedTabIndex > maxTabIndex) {
      setSelectedTabIndex(0);
    }
  }, [hasDomainForge, isReadOnly, selectedTabIndex]);

  const handleSaveAppChange = async () => {
    if (!linkedUseCaseDetail?.id) return;
    try {
      setIsSavingApp(true);
      await forgeService.mapUseCaseToApplication(linkedUseCaseDetail.id, pendingAppId);
      await refetchCadrageData();
      setAppLoadError(null);
      setShowChangeApp(false);
      setPendingAppId(null);
    } catch (err: any) {
      console.error('Failed to update application mapping', err);
    } finally {
      setIsSavingApp(false);
    }
  };

  const handleUnlinkApp = async () => {
    if (!linkedUseCaseDetail?.id) return;
    try {
      setIsUnlinkingApp(true);
      await forgeService.mapUseCaseToApplication(linkedUseCaseDetail.id, null);
      await refetchCadrageData();
      setAppLoadError(null);
      setShowChangeApp(false);
      setPendingAppId(null);
    } catch (err: any) {
      console.error('Failed to unlink application', err);
    } finally {
      setIsUnlinkingApp(false);
    }
  };

  // --- Gestion du chargement et des erreurs globales ---
  if (isLoadingCadrage && !cadrageData) {
    return (
      <PageLayout icon={<FileText className="h-6 w-6" />} title={t('cadrages.list.title', 'Cadrages')} subtitle={t('cadrage.detail.loading')}>
        <div className="text-center text-muted-foreground">{t('cadrage.detail.loading')}</div>
      </PageLayout>
    );
  }
  if (errorCadrage) {
    return (
      <PageLayout icon={<FileText className="h-6 w-6" />} title={t('cadrages.list.title', 'Cadrages')} subtitle={t('cadrage.detail.errorPrefix')}>
        <NotFoundState error={loadErrorCadrage}>
          <div className="text-center text-red-600">{t('cadrage.detail.errorPrefix')} {errorCadrage}</div>
        </NotFoundState>
      </PageLayout>
    );
  }
  if (!cadrageData) {
    return (
      <PageLayout icon={<FileText className="h-6 w-6" />} title={t('cadrages.list.title', 'Cadrages')} subtitle={t('cadrage.detail.notFound')}>
        <div className="text-center text-red-600">{t('cadrage.detail.notFound')}</div>
      </PageLayout>
    );
  }

  // Fonction pour retourner à la version active
  const handleBackToActiveVersion = async () => {
    if (!cadrageData.use_case_id) {
      notify.error(t('cadrage.detail.errors.activeVersionNotFound'));
      return;
    }

    try {
      // Fetch the active cadrage for this use case
      const versions = await cadrageService.getCadrageHistory(cadrageId || '');
      const activeVersion = versions.find((v: any) => v.is_active);

      if (activeVersion) {
        navigate(`/cadrages/${activeVersion.id}`);
      } else {
        notify.error(t('cadrage.detail.errors.noActiveVersionFound'));
      }
    } catch (error) {
      console.error(t('cadrage.detail.errors.findActiveVersionError'), error);
      notify.error(t('cadrage.detail.errors.findActiveVersionError'));
    }
  };

  // --- Rendu du Composant ---
  return (
    <PageLayout
      icon={<FileText className="h-6 w-6" />}
      title={cadrageData.name}
      subtitle={cadrageData.description || t('cadrage.detail.noDescription')}
      titleSuffix={
        cadrageId ? (
          <AskAgentButton entityType="cadrage" entityId={cadrageId} entityName={cadrageData.name} />
        ) : null
      }
    >
      <div className="space-y-6">
        {(isReadOnly && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="flex items-center space-x-1 px-3 py-1.5 bg-orange-100 text-orange-800 text-sm font-medium rounded-lg">
                <Archive className="h-4 w-4" />
                <span>{t('global.history')}</span>
              </span>
              <span className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 text-gray-800 text-sm font-medium rounded-lg">
                <Eye className="h-4 w-4" />
                <span>{t('cadrage.detail.readOnlyBadge')}</span>
              </span>
            </div>
            <p className="text-sm text-orange-600 font-medium">
              ⚠️ {t('cadrage.detail.archivedWarning', { version: cadrageData.version })}
            </p>
            <button
              onClick={handleBackToActiveVersion}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{t('cadrage.detail.backToActiveVersion')}</span>
            </button>
          </div>
        )) || null}

        {cadrageData.artifact_session_id && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-primary/30 bg-primary/5">
            <div className="flex items-center gap-2 text-sm">
              <BookOpen className="h-4 w-4 text-primary" />
              <span className="text-foreground">
                {t(
                  'cadrage.detail.artifactBadge',
                  'Généré depuis une session Genie Artifact'
                )}
              </span>
            </div>
            <button
              onClick={() =>
                navigate(`/genie-artifact/${cadrageData.artifact_session_id}`)
              }
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-primary hover:underline"
            >
              {t('cadrage.detail.openArtifact', 'Ouvrir la session')}
              <ArrowLeft className="h-3 w-3 rotate-180" />
            </button>
          </div>
        )}

        <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
          <Tab.List className="flex space-x-2 rounded-xl bg-muted p-1 mb-4 flex-wrap">
            {[
              { name: t('global.summary'), icon: Info, show: true },
              { name: t('cadrage.detail.tabs.architecture'), icon: Building2, show: true },
              { name: t('cadrage.detail.tabs.database'), icon: Database, show: true },
              { name: t('global.interviews'), icon: MessageCircle, show: true },
              { name: t('global.documents'), icon: FileText, show: true },
              { name: t('cadrage.detail.tabs.blockers'), icon: Lock, show: true },
              { name: t('global.application', 'Application'), icon: Server, show: hasDomainForge },
              { name: t('global.history', 'Historique'), icon: Clock, show: !isReadOnly },
            ].filter(tab => tab.show).map(({ name, icon: Icon }, index) => (
              <Tab
                key={index}
                className={({ selected }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center space-x-2
                  ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{name}</span>
              </Tab>
            ))}
          </Tab.List>

          <div className="bg-card rounded-lg shadow-sm border border-border">
            <Tab.Panels className="mt-0">
              {/* Onglet Synthèse */}
              <Tab.Panel className="p-6 space-y-6">
                <CadrageGeneralInfoSection
                  cadrage={cadrageData}
                  onRefresh={async () => refetchCadrageData()}
                  isReadOnly={isReadOnly}
                  onDelete={handleDeleteCadrage}
                  isDeleting={isDeleting}
                />
                {/* Ajouter la section Info Société ici */}
                <CadrageCompanyInfoSection
                  cadrage={cadrageData}
                  isLoading={isLoadingCadrage}
                  isReadOnly={isReadOnly}
                />
                <CadrageLinkedUseCaseSection
                  linkedUseCase={linkedUseCaseDetail}
                  isLoading={isLoadingCadrage}
                  onCreateClick={async () => setIsUseCaseFormModalOpen(true)}
                  isReadOnly={isReadOnly}
                />
                <CadrageTeamSection
                  teamMembers={teamMembers}
                  availableUsers={availableUsers}
                  onAddMember={addMember}
                  onRemoveMember={removeMember}
                  isLoading={isTeamLoading}
                  isReadOnly={isReadOnly}
                />
                {/* Afficher l'erreur du hook team si présente */}
                {teamError && <p className="text-red-500 text-sm mt-2">{teamError}</p>}

              </Tab.Panel>

              {/* Onglet Architecture */}
              <Tab.Panel className="p-6">
                <CadrageArchitectureFlowSection cadrageId={cadrageId} isReadOnly={isReadOnly} />
              </Tab.Panel>

              {/* Onglet Database */}
              <Tab.Panel className="p-6">
                {/* Utiliser directement le composant, il gère son état via le store */}
                <CadrageDatabaseSchemaSection isReadOnly={isReadOnly} />
              </Tab.Panel>

              {/* Onglet Entretiens */}
              <Tab.Panel className="p-6">
                <CadrageInterviewsSection
                  interviews={interviewsHook.interviews}
                  isLoading={interviewsHook.isLoading}
                  error={interviewsHook.error}
                  onTriggerNewInterview={() => setIsNewInterviewModalOpen(true)}
                  selectInterviewToEdit={interviewsHook.selectInterviewToEdit}
                  openEditModal={interviewsHook.openEditModal}
                  onViewInterview={(interview) => { setInterviewToView(interview); setIsViewModalOpen(true); }}
                  onDeleteInterview={interviewsHook.deleteInterview}
                  isReadOnly={isReadOnly}
                />
              </Tab.Panel>

              {/* Onglet Documents */}
              <Tab.Panel className="p-6">
                <DocumentManagerSection
                  context={cadrageId ? { type: 'cadrage', id: cadrageId } : undefined}
                  isReadOnly={isReadOnly}
                  onImportToAIExtraction={async (item) => {
                    try {
                      if (!item.documentId) {
                        notify.error(t('cadrage.detail.errors.documentIdNotFound'));
                        return;
                      }
                      const result = await readStoredDocument(item.documentId);
                      if (!result.success || !result.content.trim()) {
                        notify.error(result.error || t('cadrage.detail.errors.noExtractableContent'));
                        return;
                      }
                      navigate('/ai-extraction', {
                        state: {
                          initialText: result.content,
                          source: {
                            type: 'cadrage_document',
                            documentId: item.documentId,
                            filename: result.filename,
                            cadrageId,
                          },
                        },
                      });
                    } catch (e: unknown) {
                      console.error(t('cadrage.detail.errors.documentReadError'), e);
                      notify.error(
                        e instanceof Error ? e.message : t('cadrage.detail.errors.documentReadError')
                      );
                    }
                  }}
                />
              </Tab.Panel>

              {/* Onglet Verrous */}
              <Tab.Panel className="p-6">
                <CadrageBlockersSection cadrageId={cadrageId} isReadOnly={isReadOnly} />
              </Tab.Panel>

              {/* Onglet Application Forge */}
              {hasDomainForge && (
                <Tab.Panel className="p-6">
                  {isReadOnly ? (
                    <div className="text-center py-12">
                      <Server className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        {t('cadrage.detail.application.archivedTitle')}
                      </h3>
                      <p className="text-muted-foreground max-w-md mx-auto">
                        {t('cadrage.detail.application.archivedDescription')}
                      </p>
                    </div>
                  ) : !linkedUseCaseDetail ? (
                    <div className="text-center py-12">
                      <Server className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        {t('cadrage.detail.application.noUseCase', 'No linked use case')}
                      </h3>
                      <p className="text-muted-foreground max-w-md mx-auto">
                        {t('cadrage.detail.application.description', 'Link a use case to this cadrage to manage Forge applications.')}
                      </p>
                    </div>
                  ) : appLoadError ? (
                    <div className="rounded-lg border border-yellow-400/50 bg-yellow-400/10 p-4">
                      <div className="flex items-start gap-3">
                        <svg className="h-5 w-5 shrink-0 text-yellow-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                            {t('cadrage.detail.application.errorWarningTitle', 'Application inaccessible')}
                          </p>
                          <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-0.5">
                            {appLoadError}
                          </p>
                          {!showChangeApp && (
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                              <button
                                onClick={() => { setPendingAppId(linkedAppId); setShowChangeApp(true); }}
                                className="text-sm font-medium text-yellow-700 dark:text-yellow-400 underline underline-offset-2 hover:text-yellow-800 dark:hover:text-yellow-300"
                              >
                                {t('cadrage.detail.application.changeApp', 'Changer l\'application associée')}
                              </button>
                              <button
                                onClick={handleUnlinkApp}
                                disabled={isUnlinkingApp}
                                className="text-sm font-medium text-yellow-700 dark:text-yellow-400 underline underline-offset-2 hover:text-yellow-800 dark:hover:text-yellow-300 disabled:opacity-50"
                              >
                                {isUnlinkingApp ? t('global.saving', 'Enregistrement…') : t('cadrage.detail.application.unlinkApp', 'Dissocier l\'application')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {showChangeApp && (
                        <div className="mt-4 pt-4 border-t border-yellow-400/30 space-y-3">
                          <ApplicationSelector
                            forgeId={domainForgeId}
                            selectedApplicationId={pendingAppId}
                            onSelect={(appId) => setPendingAppId(appId)}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveAppChange}
                              disabled={isSavingApp || pendingAppId === linkedAppId}
                            >
                              {isSavingApp ? t('global.saving', 'Enregistrement…') : t('global.save', 'Enregistrer')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setShowChangeApp(false); setPendingAppId(null); }}
                            >
                              {t('global.cancel')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <ApplicationManagementModule
                      useCaseId={linkedUseCaseDetail.id}
                      useCaseName={linkedUseCaseDetail.name}
                      applicationId={linkedUseCaseDetail.forge_application_id}
                      domainForgeId={domainForgeId}
                      currentUserEmail={currentUser?.email || 'unknown'}
                      onApplicationCreated={() => refetchCadrageData()}
                      onApplicationDeleted={() => refetchCadrageData()}
                      onApplicationUnlinked={() => refetchCadrageData()}
                      cadrageId={cadrageId}
                      cadrageName={cadrageData?.name}
                      cadrageDescription={cadrageData?.description}
                    />
                  )}
                </Tab.Panel>
              )}

              {/* Onglet Historique - seulement visible si pas en readonly */}
              {!isReadOnly && (
                <Tab.Panel className="p-6">
                  <CadrageHistorySection cadrageId={cadrageId} />
                </Tab.Panel>
              )}

            </Tab.Panels>
          </div>
        </Tab.Group>
      </div>

      {/* --- Modales (ne s'ouvrent pas en mode read-only) --- */}
      {!isReadOnly && (
        <>
          <NewInterviewModal
            isOpen={isNewInterviewModalOpen}
            onClose={() => setIsNewInterviewModalOpen(false)}
            onSubmit={handleModalCreateInterview}
            templateType="Cadrage" // Indiquer le contexte pour filtrer les templates
          // isLoading est géré en interne par la modale via useInterviewTemplates
          />

          <UseCaseFormModal
            isOpen={isUseCaseFormModalOpen}
            onClose={() => setIsUseCaseFormModalOpen(false)}
            onSubmit={handleModalCreateUseCase}
          // Pas de données initiales pour la création
          // isLoading peut être géré localement ou via un état global si nécessaire
          />

          {/* Modale d'édition d'entretien */}
          <InterviewEditModal
            isOpen={interviewsHook.isEditModalOpen}
            onClose={interviewsHook.closeEditModal}
            interview={interviewsHook.interviewToEdit}
            onSave={handleSaveEditedInterview}
            isLoading={interviewsHook.isLoading}
          />
          <InterviewEditModal
            isOpen={isViewModalOpen}
            onClose={() => { setIsViewModalOpen(false); setInterviewToView(null); }}
            interview={interviewToView}
            onSave={async () => {}}
            isLoading={false}
            isViewOnly={true}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            open={showDeleteModal}
            title={t('cadrageDetail.deleteTitle', 'Supprimer le cadrage')}
            message={t(
              'cadrageDetail.deleteMessage',
              'Cette action supprimera définitivement la solution "{{name}}" et toutes ses données associées (verrous, livrables, entretiens, applications Forge liées). Cette action est irréversible.',
              { name: cadrageData?.name || '' }
            )}
            confirmText={t('cadrageDetail.deleteConfirm', 'Supprimer définitivement')}
            cancelText={t('global.cancel')}
            variant="danger"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            loading={isDeleting}
            requireTextMatch={{
              text: cadrageData?.name || '',
              label: t('cadrageDetail.deleteInputLabel', 'Tapez le nom du cadrage pour confirmer'),
            }}
            inputValue={cadrageNameInput}
            onInputChange={setCadrageNameInput}
          />
        </>
      )}
    </PageLayout>
  );
};

export default CadrageDetailPage;
